<template>
  <div
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
    class="w-full h-screen max-h-screen bg-primary flex flex-col select-none papsi-main-wrapper"
    style="overscroll-behavior-y: contain; touch-action: pan-y"
  >
    <div
      class="absolute top-0 inset-x-0 w-full flex items-center justify-center z-10"
      :style="{
        transform: `translateY(${spinnerOffset}px)`,
        opacity: spinnerOffset > 0 ? 1 : 0,
        transition: 'transform 0.2s, opacity 0.2s',
        height: '60px',
        pointerEvents: 'none',
      }"
    >
      <div
        class="flex items-center justify-center w-12 h-12 bg-gray-200 rounded-full bg-white shadow-lg"
      >
        <img
          class="h-refresh w-refresh"
          src="@/assets/icons/papsi_spinner_green.svg"
          alt="Loading..."
        />
      </div>
    </div>

    <div v-if="navbar.show && !maintenance && !loading">
      <NavbarSection
        @opensidebar="openSidebar"
        @logout="logout"
        @showloader="showLoader"
        @hideloader="hideLoader"
        v-bind:noback="!navbar.back"
        v-bind:type="navbar.type"
        v-bind:menu="navbar.menu"
        v-bind:title="navbar.title"
      />
    </div>
    <div
      ref="main_container"
      class="flex-grow bg-grey-lighter overflow-y-auto overflow-x-hidden"
    >
      <div v-if="maintenance">
        <MaintenanceModeSection />
      </div>
      <div v-if="!maintenance">
        <div
          v-show="blinder"
          class="w-full h-screen fixed z-modal-screen papsi-blinder-screen"
        ></div>
        <LoadingScreen v-show="loading" />
        <MessageBoxControl
          @closed="closeMessageBox"
          v-bind:show="message.show"
          v-bind:theme="message.theme"
          v-bind:title="message.title"
          v-bind:message="message.message"
        />
        <SidebarSection
          @closed="closeSidebar"
          @showloader="showLoader"
          @hideloader="hideLoader"
          @terminate="terminated"
          @logout="logoutNow"
          v-bind:show="sidebar && login && loaded"
        />
        <div v-if="loaded" v-show="!loading">
          <router-view
            @success="success"
            @error="error"
            @alert="alert"
            @login="loginNow"
            @logout="logoutNow"
            @showloader="showLoader"
            @hideloader="hideLoader"
            @showblinder="showBlinder"
            @hideblinder="hideBlinder"
            @terminate="terminated"
            @checkauth="checkAuth"
            @nofooter="nofooter"
            @shownavbar="showNavbar"
            @hidenavbar="hideNavbar"
            @opensidebar="openSidebar"
          />
        </div>
      </div>
    </div>
    <div v-if="!maintenance && !loading && login && !no_footer">
      <FooterSection />
    </div>
  </div>
</template>

<script>
import {
  isMobile,
  isTablet,
  isWinPhone,
  isAndroid,
  isMobileSafari,
  isIOS,
  isIOS13,
  isIPad13,
  isIPhone13,
} from "mobile-device-detect";
import FooterSection from "./components/sections/FooterSection.vue";
import LoadingScreen from "./components/sections/LoadingScreenSection.vue";
import MaintenanceModeSection from "./components/sections/MaintenanceModeSection.vue";
import MessageBoxControl from "./components/controls/MessageBoxControl.vue";
import NavbarSection from "./components/sections/NavbarSection.vue";
import SidebarSection from "./components/sections/SidebarSection.vue";
import mixin from "./mixin.js";
import preload from "./preload.js";
import User from "./services/User.js";

export default {
  name: "app",
  mixins: [mixin, preload],
  components: {
    FooterSection,
    LoadingScreen,
    MaintenanceModeSection,
    MessageBoxControl,
    NavbarSection,
    SidebarSection,
  },
  data: function () {
    return {
      login: false,
      loading: true,
      loaded: false,
      sidebar: false,
      no_footer: false,
      maintenance: false,
      user_agent: null,
      blinder: false,
      navbar: {
        type: null,
        show: false,
        back: true,
        menu: true,
        title: null,
      },
      message: {
        theme: "success",
        title: "",
        message: "",
        show: false,
        href: null,
        type: null,
      },
      start_y: 0,
      pull_distance: 0,
      is_pulling: false,
      ga_mode: process.env.VUE_APP_ELECTION_ENABLED.toString() == "true",
    };
  },
  mounted() {
    let that = this;

    this.hideNavbar();

    if (
      isMobile ||
      isTablet ||
      isWinPhone ||
      isAndroid ||
      isMobileSafari ||
      isIOS ||
      isIOS13 ||
      isIPad13 ||
      isIPhone13
    ) {
      this.maintenance =
        process.env.VUE_APP_MAINTENANCE_MODE.toString() == "true";

      if (!this.maintenance) {
        this.preloadStart(function () {
          that.preloadingDone();
          that.setUserAgent(navigator.userAgent);
        });
      }
    } else {
      window.location.href = process.env.VUE_APP_WEB_URL + "?from=mobile";
    }
  },
  computed: {
    spinnerOffset() {
      return Math.min(this.pull_distance, 80);
    },
  },
  methods: {
    alert(config) {
      this.message.theme = config.theme;
      this.message.title = config.title;
      this.message.message = config.message;
      this.message.href = config.href;
      this.message.type = config.type;
      this.message.show = true;
    },
    closeMessageBox() {
      this.message.show = false;
      this.$emit("alert_closed");

      if (this.message.href != null) {
        this.$router.push(this.message.href);
      }

      if (this.message.type === "logout") {
        this.logout();
        this.logoutNow();
        this.destroyCookies();
        this.resetUserData();
      }

      this.message.theme = "success";
      this.message.title = "";
      this.message.message = "";
      this.message.href = null;
      this.message.type = null;
    },
    showNavbar(obj) {
      this.navbar.title =
        typeof obj.title !== "undefined" ? obj.title : this.lang("lbl_0000008");
      this.navbar.type = typeof obj.type !== "undefined" ? obj.type : "normal";
      this.navbar.back = typeof obj.back !== "undefined" ? obj.back : true;
      this.navbar.menu = typeof obj.menu !== "undefined" ? obj.menu : false;
      this.navbar.show = typeof obj.show !== "undefined" ? obj.show : true;
    },
    hideNavbar() {
      this.navbar.show = false;
    },
    preloadingDone() {
      if (!this.maintenance) {
        this.checkCookies();
      } else {
        this.$store.state.down = true;
        this.loading = false;
        this.loaded = true;
      }
    },
    checkAuth(status = true) {
      let state = this.$store.state;

      // if user is required to be logged in
      if (status) {
        // if hash is not saved in cookie
        if (!this.$cookies.isKey("hash")) {
          this.destroyCookies();
          this.resetUserData();
          this.$router.push("/");
        } else {
          // if hash is saved in cookie and account is deactivated
          if (state.deactivated) {
            this.$router.push("/user/deactivated");
            return;
          }

          // if hash is saved in cookie and user has no pin
          if (!state.has_pin || (state.has_pin && state.pin_update)) {
            this.$router.push("/user/enroll/pin");
            return;
          }
        }
      }

      // if user is not required to be logged in
      else {
        // if hash is saved in cookie
        if (this.$cookies.isKey("hash")) {
          // if hash is saved in cookie and account is deactivated
          if (state.deactivated) {
            this.$router.push("/user/deactivated");
            return;
          }

          // if hash is saved in cookie and user has no pin
          if (!state.has_pin || (state.has_pin && state.pin_update)) {
            this.$router.push("/user/enroll/pin");
            return;
          }

          if (
            state.login &&
            state.pin_login &&
            (!state.config.some(
              (item) => item.type === 2 && item.status === true,
            ) ||
              state.otp_login)
          ) {
            this.$emit("login");
            this.$router.push("/dashboard");
            this.hideLoader();
            return;
          }
        }
      }
    },
    checkCookies() {
      let state = this.$store.state;

      if (this.$cookies.isKey("hash")) {
        User.fetchSession()
          .then((response) => {
            this.login = true;
            this.setUserData(response.data.data);

            if (this.$cookies.get("pin_login") == state.hash) {
              state.pin_login = true;
            }

            if (this.$cookies.get("otp_login") == state.hash) {
              state.otp_login = true;
            }

            if (this.ga_mode) {
              this.$store.state.ga.user.can_participate =
                response.data.data.ga.can_participate;

              this.$store.state.ga.user.has_participated =
                response.data.data.ga.has_participated;

              this.$store.state.ga.user.has_registered =
                response.data.data.ga.has_registered;
            }

            this.hideLoader();
            this.loaded = true;
          })
          .catch((error) => {
            console.log(error);

            if (error.response && error.response.status === 403) {
              this.$emit("hidenavbar");

              state.pin_login = false;
              state.otp_login = false;

              this.destroyCookies();
              this.resetUserData();
              this.$router.push("/");

              this.login = false;
            }

            this.hideLoader();
            this.loaded = true;

            this.error(error);
          });
      } else {
        this.$emit("hidenavbar");

        this.destroyCookies();
        this.hideLoader();
        this.resetUserData();

        this.loaded = true;
      }
    },
    loginNow() {
      this.loaded = true;
      this.login = true;
      this.sidebar = false;
    },
    logoutNow() {
      this.loaded = true;
      this.login = false;
      this.sidebar = false;
    },
    logout() {
      this.showLoader();
      User.logout()
        .then(() => {
          this.destroyCookies();
          this.resetUserData();
        })
        .catch((error) => {
          let response = error.response.data,
            status = response.status;
          if (status == 403) {
            this.hideLoader();
            this.hideNavbar();
            this.$router.push("/");
            return;
          }
          this.error(error);
        })
        .finally(() => {
          this.hideLoader();
          this.hideNavbar();
          this.$router.push("/");
        });
    },
    goToLogin() {
      window.location.href =
        process.env.VUE_APP_BASE_URL + "?t=" + new Date().getTime().toString();
    },
    showLoader() {
      this.loading = true;
    },
    hideLoader() {
      this.loading = false;
    },
    showBlinder() {
      this.blinder = true;
    },
    hideBlinder() {
      this.blinder = false;
    },
    openSidebar() {
      this.sidebar = true;
    },
    closeSidebar() {
      this.sidebar = false;
    },
    nofooter(v = true) {
      this.no_footer = v;
    },
    terminated(status) {
      if (status == 403) {
        let title = this.lang("log.in"),
          message = this.lang("please.login.to.your.account");

        this.hideLoader();
        this.destroyCookies();
        this.alert({
          theme: "error",
          title: title,
          message: message,
        });
      }
    },
    error(error) {
      let response = error.response.data,
        status = response.status,
        state = this.$store.state;

      if (status == 403) {
        this.alert({
          theme: "error",
          title: this.lang("lbl_0000034"),
          message: this.lang("lbl_0000055"),
          type: state.login ? "logout" : null,
          href: "/",
        });
        this.$router.push("/");
        this.destroyCookies();
        this.resetUserData();
        this.$emit("hidenavbar");
        this.hideLoader();
      } else {
        this.alert({
          theme: "error",
          title: this.lang("lbl_0000034"),
          message: response.message,
        });
      }

      this.hideLoader();
    },
    success(message) {
      this.alert({
        theme: "success",
        title: this.lang("lbl_0000058"),
        message: message,
      });

      this.hideLoader();
    },
    handleTouchStart(event) {
      if (this.loading) return;

      this.start_y = event.touches[0].clientY;
      this.is_pulling = false;

      const safe_inset_top = window.safeAreaInsets?.top || 40; // fallback 40px
      const start_buffer = 40; // extra buffer for safety
      this.can_pull = this.start_y <= safe_inset_top + start_buffer;
    },
    handleTouchMove(event) {
      if (this.loading || this.$store.state.is_pdf_open || !this.can_pull)
        return;

      const current_y = event.touches[0].clientY;
      const raw_distance = current_y - this.start_y;
      const max_pull_distance = window.innerHeight / 3;

      // only trigger pull-down if we’re starting near top and pulling down
      if (this.can_pull && raw_distance > 0) {
        this.is_pulling = true;
        this.pull_distance = Math.min(raw_distance, max_pull_distance);
      } else {
        this.is_pulling = false;
        this.pull_distance = 0;
      }
    },

    handleTouchEnd() {
      if (
        !this.loading &&
        this.is_pulling &&
        this.pull_distance > 100 &&
        !this.$store.state.is_pdf_open
      ) {
        window.location.reload(true);
      }
      this.is_pulling = false;
      this.pull_distance = 0;
      this.can_pull = false;
    },
  },
  watch: {
    $route() {
      this.$refs.main_container.scrollTop = 0;
    },
  },
};
</script>

<style>
body {
  width: 100%;
  height: 100vh;
  font-family: "Poppins", sans-serif;
}

.papsi-blinder-screen {
  background-color: rgba(0, 0, 0, 0.85);
  top: 0px;
  left: 0px;
}

.papsi-main-wrapper {
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  background-color: var(--color-primary, #016b00);
}

html,
body {
  overscroll-behavior-y: contain;
  touch-action: pan-y;
}
</style>
